import { portugueseCore } from "@/data/portuguese-core";
import { portugueseSite } from "@/data/portuguese-site";
import { germanSite } from "@/data/german-site";
import { homeCatalogs } from "@/data/world-home";
import { bioCatalogs } from "@/data/bio-site";
import { cvCatalogs } from "@/data/cv-site";
import { pageCatalogs } from "@/data/pages-site";
import { themeCatalogs } from "@/data/themes-site";

// Shared on globalThis: in development the JSX runtime is pre-bundled with its own copy of
// this module, and both copies must read the same language.
const store = globalThis as unknown as { __ljpRenderLang?: string };
store.__ljpRenderLang ??= typeof document !== "undefined" ? document.documentElement.lang || "en" : "en";
/** Set by the language provider on every render. */
export function setRenderLang(lang: string) {
  store.__ljpRenderLang = lang;
}
/** Static catalogs per language, keyed by the English source text. */
const CATALOGS: Record<string, Array<Record<string, string>>> = {
  pt: [portugueseCore, portugueseSite],
  ...Object.fromEntries(Object.keys(pageCatalogs).map((l): [string, Array<Record<string, string>>] => [l, [bioCatalogs[l] ?? {}, pageCatalogs[l] ?? {}]])),
  es: [bioCatalogs["es"] ?? {}, pageCatalogs["es"] ?? {}],
  ...Object.fromEntries(Object.entries(homeCatalogs).map(
    ([l, c]): [string, Array<Record<string, string>>] => [
      l, [bioCatalogs[l] ?? {}, cvCatalogs[l] ?? {}, pageCatalogs[l] ?? {}, ...(l === "de" ? [germanSite] : []), c],
    ],
  )),
};
// Theme-page copy (titles, topic lines, introductions) applies to every announced language,
// on top of the catalogs above; existing entries keep priority.
for (const [lang, catalog] of Object.entries(themeCatalogs)) {
  CATALOGS[lang] = [...(CATALOGS[lang] ?? []), catalog];
}

const lookup = (k: string, lang = store.__ljpRenderLang!) => {
  for (const c of CATALOGS[lang] ?? []) if (c[k]) return c[k];
  return undefined;
};
export const isLocalizing = () => store.__ljpRenderLang! in CATALOGS;

const SKIP = new Set([
  "className", "href", "src", "to", "id", "key", "slug", "poster", "style", "type", "rel",
  "target", "name", "property", "charSet", "params", "search", "hash", "srcSet", "value",
  "defaultValue", "htmlFor", "d", "viewBox", "role", "lang", "dangerouslySetInnerHTML",
  "vimeo", "youtube", "videoSrc", "webm", "cover", "url", "file", "image", "thumb",
]);

export function translateText(text: string, lang = store.__ljpRenderLang!): string {
  const k = text.replace(/\s+/g, " ").trim();
  if (!k) return text;
  let hit = lookup(k, lang);
  if (!hit) {
    // Page titles such as "Project — Luiza Jesus do Prado": translate the first part.
    const m = k.match(/^(.+?)( — Luiza Jesus do Prado| \| Luiza Jesus do Prado)$/);
    const head = m && lookup(m[1]!, lang);
    if (!head) return text;
    hit = head + m![2];
  }
  // Keep the surrounding spaces of JSX text so words do not stick together.
  const lead = text.match(/^\s*/)![0];
  const trail = text.match(/\s*$/)![0];
  return lead + hit + trail;
}

// One cache per language: shared data objects must not keep another language's result.
const caches = new Map<string, WeakMap<object, unknown>>();
function tr(value: unknown, depth: number): unknown {
  if (typeof value === "string") return translateText(value);
  if (depth > 4 || value === null || typeof value !== "object" || "$$typeof" in value) return value;
  const lang = store.__ljpRenderLang!;
  let cache = caches.get(lang);
  if (!cache) caches.set(lang, (cache = new WeakMap()));
  const cached = cache.get(value);
  if (cached) return cached;
  let out: unknown = value;
  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map((v) => {
      const t = tr(v, depth + 1);
      if (t !== v) changed = true;
      return t;
    });
    if (changed) out = next;
  } else if (Object.getPrototypeOf(value) === Object.prototype) {
    let changed = false;
    const next: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      const t = SKIP.has(k) ? v : tr(v, depth + 1);
      if (t !== v) changed = true;
      next[k] = t;
    }
    if (changed) out = next;
  }
  cache.set(value, out);
  return out;
}

export function localizeProps(props: any, force?: boolean) {
  if (force === false) return props;
  if (!props || typeof props !== "object") return props;
  let next: any = null;
  for (const k in props) {
    if (SKIP.has(k)) continue;
    const v = props[k];
    if (typeof v === "function") continue;
    if (v && typeof v === "object" && "$$typeof" in v) continue;
    const t = k === "children" && Array.isArray(v) ? v.map((c) => tr(c, 4)) : tr(v, 0);
    if (t !== v && !(Array.isArray(t) && t.every((x, i) => x === v[i]))) {
      next ??= { ...props };
      next[k] = t;
    }
  }
  return next ?? props;
}

// The JSX runtime picks this up from globalThis (see src/lib/pt-jsx/jsx-runtime.ts).
(globalThis as unknown as { __ljpLocalize?: (p: any) => any }).__ljpLocalize = (props: any) =>
  isLocalizing() ? localizeProps(props) : props;
